<script setup lang="ts">
import IconChevron from '@/components/icons/IconChevron.vue'
import OkaneKollegeUserPageSidebarItem from './OkaneKollegeUserPageSidebarItem.vue'
import { useRoute } from 'vue-router'
import UserTrialStatusChip from '@/features/okane-kollege/UserTrialStatusChip/UserTrialStatusChip.vue'
import UserCourseStatusChip from '@/features/okane-kollege/UserCourseStatusChip/UserCourseStatusChip.vue'
import type { OkaneKollegeUserPageSidebarKollegeUserFragment } from './OkaneKollegeUserPageSidebar.generated'
import OkaneKollegeUserPageSidebarMemo from './OkaneKollegeUserPageSidebarMemo.vue'
import { computed } from 'vue'
import { useEntranceGUIDParam } from '@/features/entrance/useEntranceGUIDParam'

// route.nameでサイドバーのどこが選択中なのか判定するロジックがあるが
// そこのroute.nameはEditProfile以外仮
const route = useRoute()

const { entranceGUID } = useEntranceGUIDParam({ must: true })

const props = defineProps<{
  user: OkaneKollegeUserPageSidebarKollegeUserFragment
}>()

const profile = computed(() => {
  if (!props.user?.profile) return null

  return {
    familyName: props.user.profile.familyName,
    givenName: props.user.profile.givenName,
  }
})
</script>
<template>
  <section :class="$style.sidebar">
    <header :class="$style.sidebar__header">
      <div :class="$style.backToList">
        <IconChevron direction="left" />
        <RouterLink to="/kollege">一覧に戻る</RouterLink>
      </div>
      <div :class="$style.profile" v-if="profile">
        <UserTrialStatusChip :user="user" />
        <UserCourseStatusChip :user="user" />
        <span :class="$style.profile__name"
          >{{ profile.familyName }} {{ profile.givenName }}</span
        >
      </div>
      <div :class="$style.memoWrapper">
        <OkaneKollegeUserPageSidebarMemo :fragment="user" />
      </div>
    </header>
    <div :class="$style.sidebar__list">
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeTrial',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeTrial'"
      >
        無料体験会
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeUserCourse',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeUserCourse'"
      >
        コース
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeUserCourseLesson',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeUserCourseLesson'"
      >
        パーソナルレッスン
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeDailyLesson',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeDailyLesson'"
      >
        デイリーレッスン
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeUserPayment',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeUserPayment'"
      >
        支払い
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeEditProfile',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeEditProfile'"
      >
        アカウント
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        :to="{
          name: 'OkaneKollegeUserCourseCancel',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        :is-selected="route.name === 'OkaneKollegeUserCourseCancel'"
      >
        解約処理
      </OkaneKollegeUserPageSidebarItem>
      <OkaneKollegeUserPageSidebarItem
        is-external
        :to="{
          name: 'OkaneKollegeUser',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        >Driveリンク</OkaneKollegeUserPageSidebarItem
      >
      <OkaneKollegeUserPageSidebarItem
        is-external
        :to="{
          name: 'OkaneKollegeUser',
          params: {
            entranceGUID,
            userId: user.id,
          },
        }"
        >公式LINEチャット</OkaneKollegeUserPageSidebarItem
      >
    </div>
  </section>
</template>

<style lang="scss" module>
.sidebar {
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-4;
  display: flex;
  flex-direction: column;
  width: 268px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  & > *:nth-child(n + 2) {
    border-top: 1px solid $color-border-low-emphasis;
  }

  &__list {
    margin-top: 16px;

    & > *:nth-child(n + 2) {
      border-top: 1px solid $color-border-low-emphasis;
    }
  }
}

.backToList {
  padding: 12px 16px;
  display: inline-flex;
  align-items: center;
}

.profile {
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  &__name {
    font-size: 20px;
    font-weight: bold;
  }
}

.memoWrapper {
  margin-top: 8px;
  padding: 0 16px;
}

.buttonGroup {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
